/**
 * HeaderComponent - Responsive header with navigation and mobile menu
 * Implements the HeaderComponent interface with mobile menu toggle functionality
 */

import { HeaderComponent as IHeaderComponent, NavigationItem } from '../types/components';

export class HeaderComponent implements IHeaderComponent {
  public logo: string;
  public navigation: NavigationItem[];
  public mobileMenuToggle: boolean;
  
  private headerElement: HTMLElement | null;
  private mobileToggleButton: HTMLButtonElement | null;
  private navElement: HTMLElement | null;
  private isMenuOpen: boolean = false;

  constructor(logo: string = 'Kiwi Voyages', navigation: NavigationItem[] = []) {
    this.logo = logo;
    this.navigation = navigation.length > 0 ? navigation : this.getDefaultNavigation();
    this.mobileMenuToggle = false;
    
    this.headerElement = null;
    this.mobileToggleButton = null;
    this.navElement = null;
    
    this.init();
  }

  /**
   * Initialize the header component
   */
  private init(): void {
    this.bindElements();
    this.attachEventListeners();
    this.updateActiveNavigation();
  }

  /**
   * Bind DOM elements
   */
  private bindElements(): void {
    this.headerElement = document.querySelector('.header');
    this.mobileToggleButton = document.querySelector('.header__mobile-toggle');
    this.navElement = document.querySelector('.header__nav');
    
    if (!this.headerElement) {
      console.warn('HeaderComponent: Header element not found');
      return;
    }
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (this.mobileToggleButton) {
      this.mobileToggleButton.addEventListener('click', this.toggleMobileMenu.bind(this));
    }

    // Close mobile menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Handle navigation clicks
    this.attachNavigationListeners();
  }

  /**
   * Attach navigation link event listeners
   */
  private attachNavigationListeners(): void {
    const navLinks = document.querySelectorAll('.header__nav-link');
    navLinks.forEach(link => {
      link.addEventListener('click', this.handleNavigationClick.bind(this));
    });
  }

  /**
   * Toggle mobile menu
   */
  public toggleMobileMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.mobileMenuToggle = this.isMenuOpen;
    
    if (this.mobileToggleButton && this.navElement && this.headerElement) {
      // Update button aria-expanded
      this.mobileToggleButton.setAttribute('aria-expanded', this.isMenuOpen.toString());
      
      // Update button label
      const label = this.isMenuOpen ? 'Fermer le menu de navigation' : 'Ouvrir le menu de navigation';
      this.mobileToggleButton.setAttribute('aria-label', label);
      
      // Toggle classes
      this.headerElement.classList.toggle('header--menu-open', this.isMenuOpen);
      this.mobileToggleButton.classList.toggle('header__mobile-toggle--active', this.isMenuOpen);
      this.navElement.classList.toggle('header__nav--open', this.isMenuOpen);
      
      // Prevent body scroll when menu is open
      document.body.classList.toggle('no-scroll', this.isMenuOpen);
    }
  }

  /**
   * Close mobile menu
   */
  public closeMobileMenu(): void {
    if (this.isMenuOpen) {
      this.toggleMobileMenu();
    }
  }

  /**
   * Handle clicks outside the header
   */
  private handleOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    if (this.isMenuOpen && this.headerElement && !this.headerElement.contains(target)) {
      this.closeMobileMenu();
    }
  }

  /**
   * Handle window resize
   */
  private handleResize(): void {
    // Close mobile menu on desktop breakpoint
    if (window.innerWidth >= 768 && this.isMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Handle navigation link clicks
   */
  private handleNavigationClick(event: Event): void {
    const target = event.target as HTMLAnchorElement;
    const href = target.getAttribute('href');
    
    // Close mobile menu when navigating
    if (this.isMenuOpen) {
      this.closeMobileMenu();
    }
    
    // Update active navigation
    this.setActiveNavigation(href || '');
  }

  /**
   * Set active navigation item
   */
  public setActiveNavigation(href: string): void {
    const navLinks = document.querySelectorAll('.header__nav-link');
    
    navLinks.forEach(link => {
      const linkHref = link.getAttribute('href');
      const isActive = linkHref === href || (href === '/' && linkHref === '/');
      
      link.classList.toggle('header__nav-link--active', isActive);
      
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
    
    // Update navigation data
    this.navigation = this.navigation.map(item => ({
      ...item,
      active: item.href === href || (href === '/' && item.href === '/')
    }));
  }

  /**
   * Update active navigation based on current page
   */
  private updateActiveNavigation(): void {
    const currentPath = window.location.pathname;
    this.setActiveNavigation(currentPath);
  }

  /**
   * Get default navigation items
   */
  private getDefaultNavigation(): NavigationItem[] {
    const basePath = (window as any).__BASE_PATH__ || '';
    return [
      { label: 'Accueil', href: `${basePath}/`, active: true },
      { label: 'Vols', href: `${basePath}/flights.html`, active: false },
      { label: 'Hôtels', href: `${basePath}/hotels.html`, active: false },
      { label: 'Trains', href: `${basePath}/trains.html`, active: false }
    ];
  }

  /**
   * Update navigation items
   */
  public updateNavigation(navigation: NavigationItem[]): void {
    this.navigation = navigation;
    // Re-render navigation if needed
    this.renderNavigation();
  }

  /**
   * Render navigation items (if dynamically updating)
   */
  private renderNavigation(): void {
    const navList = document.querySelector('.header__nav-list');
    if (!navList) return;
    
    navList.innerHTML = '';
    
    this.navigation.forEach(item => {
      const listItem = document.createElement('li');
      listItem.className = 'header__nav-item';
      
      const link = document.createElement('a');
      link.href = item.href;
      link.className = `header__nav-link${item.active ? ' header__nav-link--active' : ''}`;
      link.textContent = item.label;
      
      if (item.active) {
        link.setAttribute('aria-current', 'page');
      }
      
      listItem.appendChild(link);
      navList.appendChild(listItem);
    });
    
    // Re-attach navigation listeners
    this.attachNavigationListeners();
  }

  /**
   * Destroy the component and clean up event listeners
   */
  public destroy(): void {
    if (this.mobileToggleButton) {
      this.mobileToggleButton.removeEventListener('click', this.toggleMobileMenu.bind(this));
    }
    
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    // Remove navigation listeners
    const navLinks = document.querySelectorAll('.header__nav-link');
    navLinks.forEach(link => {
      link.removeEventListener('click', this.handleNavigationClick.bind(this));
    });
    
    // Remove no-scroll class if menu was open
    if (this.isMenuOpen) {
      document.body.classList.remove('no-scroll');
    }
  }
}